import { useState } from 'react';
import {
    IComponentCss,
    VerticalTabsCustom,
    TitlePageH1,
    FetchAutocomplete,
} from '@tap/mui-ui';
import { Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TabComponent, TabLabel, TabsTitle } from '../ReportParameterElements';
import { IApiUserAccount, IssueUploadingUsersReportResult, docAnalyticsService, userService } from '@tap/data-providers';
import { LoadingButton } from '@mui/lab';
import { IssueUploadingUsersTable } from './IssueUploadingUsersTable';
import { NotDataForReportPage } from '../../not-data-for-report/NotDataForReportPage';

/*
*   Отчёт "Загрузка пользователя" для заявок
*/

enum CurrentWindow {
    Options = 'options',
    Chart = 'chart',
    NotData = 'not-data',
}

const css: IComponentCss = {
    titleButton: {
        marginTop: '10px',
        minWidth: '189px',
    },
    contentWrap: {
        padding: '0px',
        marginBottom: '34px',
    },
    title: {
        width: '100%',
        marginBottom: '16px',
    },
    errorText: {
        margin: '4px 0px 34px 14px',
        padding: 0,
        fontSize: '12px',
    },
};

type IssueUploadingUsersPageProps = {
    title: string,
}

type IssueUploadingUsersPageFormProps = {
    worker: IApiUserAccount | undefined,
}

export default function IssueUploadingUsersPage({ title }: IssueUploadingUsersPageProps) {
    const [currentWindow, setCurrentWindow] = useState<CurrentWindow>(CurrentWindow.Options);
    const [reportData, setReportData] = useState<IssueUploadingUsersReportResult>();

    // Запрос расчета
    const { values, setFieldValue, errors, touched, ...formik } = useFormik<IssueUploadingUsersPageFormProps>({
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            worker: undefined,
        },
        validationSchema: Yup.object({
            worker: Yup.object().required('Выберите исполнителя'),
        }),
        onSubmit: async (data) => {
            const params = {
                workerId: data.worker.id!,
            }
            await docAnalyticsService.getIssueUploadingUsersReport(params)
                .then((res) => {
                    if (!res.rows.length) {
                        setCurrentWindow(CurrentWindow.NotData);
                        return;
                    }
                    setReportData(res);
                    setCurrentWindow(CurrentWindow.Chart);
                })
        },
    });

    return (
        <>

            {currentWindow === CurrentWindow.Options &&
                <>
                    <Grid
                        container
                        mb={4}
                        justifyContent='space-between'
                        sx={{ marginBottom: 0 }}
                    >
                        <TitlePageH1>
                            {title}
                        </TitlePageH1>
                        <Grid>
                            <LoadingButton
                                variant='contained'
                                color='primary'
                                loading={formik.isSubmitting}
                                disabled={
                                    formik.isSubmitting
                                    || !formik.isValid
                                }
                                sx={css.titleButton}
                                onClick={() => {
                                    formik.submitForm();
                                }}
                            >
                                Запрос отчёта
                            </LoadingButton>
                        </Grid>
                    </Grid>
                    <VerticalTabsCustom
                        isCacheTabs
                        defaultTab={1}
                        height={'calc(100vh - 210px)'}
                        tabsArray={[
                            {   // Выберите параметры - заголовок табов
                                tabButton: {
                                    label:
                                        <TabsTitle
                                            title='Параметры'
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        disabled: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent: <></>,
                            },
                            {   // Общие
                                tabButton: {
                                    label:
                                        <TabLabel
                                            title={'Общие'}
                                            subTitle={''}
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent:
                                    <TabComponent title='Общие'>
                                        <>
                                            <Grid
                                                container
                                                sx={{ ...css.contentWrap, marginBottom: 0 }}
                                            >
                                                <Typography sx={css.title}>
                                                    Исполнитель
                                                </Typography>
                                                <FetchAutocomplete
                                                    fetchRequest={({ searchText }) => {
                                                        return userService.getWorkerList({ searchText }).then((res) => res);
                                                    }}
                                                    value={values.worker}
                                                    onChange={(event: any, newValue: IApiUserAccount | null) => {
                                                        setFieldValue('worker', newValue);
                                                    }}
                                                    textFieldProps={{
                                                        name: 'worker',
                                                    }}
                                                    getOptionLabel={(option) => userService.getUserFIO(option)}
                                                    noOptionsText={'Пользователь не найден'}
                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                />
                                            </Grid>
                                            {errors.worker && touched.worker &&
                                                <Typography sx={css.errorText} color={'error'}>{errors.worker}</Typography>
                                            }
                                        </>
                                    </TabComponent>,
                            },
                        ]
                        }
                    />
                </>
            }

            {/* График */}
            {currentWindow === CurrentWindow.Chart &&
                <IssueUploadingUsersTable
                    executor={values.worker!}
                    reportData={reportData!}
                    onOptionsButtonClick={() => setCurrentWindow(CurrentWindow.Options)}
                />
            }

            {/* Заглушка на случай, если нет данных для формирования отчета. */}
            {currentWindow === CurrentWindow.NotData &&
                <NotDataForReportPage onReturnButtonClick={() => setCurrentWindow(CurrentWindow.Options)} />
            }
        </>
    );
}
